import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { RequireRole } from "../../auth/RequireRole";
import { ApiError, describeError } from "../../api/client";
import type { AdminAttachmentSummary, AdminTemplatePreview, AdminTemplateSummary } from "../../api/types";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { DetailGroup } from "../../components/DetailGroup";
import { FeedbackPanel } from "../../components/FeedbackPanel";
import { NotFoundCard } from "../../components/NotFoundCard";
import { SegmentedControl } from "../../components/SegmentedControl";
import { StatusPill } from "../../components/StatusPill";
import { useUnsavedChangesBlocker } from "../../state/routeState";
import { useQuery } from "../../state/useQuery";
import { getTemplatePreview, listAttachments, listTemplates, replaceTemplateContent, softDeleteTemplate } from "./api";
import { resolveTemplateStatus } from "./status";
import { TemplateAttachmentsPanel } from "./TemplateAttachmentsPanel";
import { TemplateReplaceEditor } from "./TemplateReplaceEditor";

type ReplaceState = { status: "idle" } | { status: "pending" } | { status: "error"; message: string };
type SoftDeleteState = { status: "idle" } | { status: "pending" } | { status: "error"; message: string };

function buildSoftDeleteConfirmation(templateName: string): string {
  return `¿Desactivar la plantilla "${templateName}"? No podrá usarse en campañas nuevas.`;
}

/**
 * `/plantillas/:templateId` — routed template detail (REDISENO-PANEL.md
 * §8.2, panel-redesign-v3 T8). Full rewrite from the pre-redesign read-only
 * detail screen: "‹ Plantillas" back link; H1 + `StatusPill`; top-right
 * "Reemplazar contenido" + red-bordered "Desactivar" (gated Operador — this
 * screen is no longer read-only); a 2-column grid where Vista previa spans
 * 2 columns (Escritorio/Móvil segmented + a 420px-min-height gray canvas,
 * or "Plantilla inactiva — sin vista previa." when inactive) and
 * Información + Adjuntos stack in the remaining column. The blob path is no
 * longer shown anywhere (§8.2: "La ruta técnica del blob deja de
 * mostrarse.").
 *
 * No dedicated `GET panel/templates/{id}` endpoint exists — this reuses the
 * same tenant-scoped `GET panel/templates` list the list screen fetches and
 * looks up the record by `templateId`, same reasoning the pre-redesign
 * screen already documented.
 */
export function TemplateDetailScreen() {
  const { templateId } = useParams<{ templateId: string }>();
  // Codex finding (PR #387 review round 3): the route-change effect below
  // only resets write state SYNCHRONOUSLY at the moment of a `templateId`
  // transition — it cannot stop an already-in-flight soft-delete promise
  // for the PREVIOUS template from resolving afterward and writing into
  // this (reused) screen instance's shared state as if it belonged to the
  // newly-selected template. A ref (not the closure's own frozen
  // `templateId`) lets an async completion check the CURRENT templateId at
  // the moment it actually settles.
  const currentTemplateIdRef = useRef(templateId);
  currentTemplateIdRef.current = templateId;
  const [previewWidth, setPreviewWidth] = useState<"desktop" | "mobile">("desktop");
  const [replacing, setReplacing] = useState(false);
  const [replaceHtml, setReplaceHtml] = useState("");
  const [replaceState, setReplaceState] = useState<ReplaceState>({ status: "idle" });
  const [softDeleteState, setSoftDeleteState] = useState<SoftDeleteState>({ status: "idle" });
  const [confirmingSoftDelete, setConfirmingSoftDelete] = useState(false);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  // Native advisory (TemplateDetailScreen.tsx:~121): a successful replace's
  // own follow-up refetch (template list + preview) can fail independently
  // of the replace itself -- this note reports THAT failure separately, so
  // it never gets mistaken for (or masks) a failed content replace.
  const [refreshNote, setRefreshNote] = useState<string | null>(null);

  // P1 (Codex PR #387, id 4377321304): React Router reuses this screen
  // INSTANCE for a direct `/plantillas/:templateId` param change (the route
  // pattern and element identity never change, only the param) — none of
  // the write/dialog state above was ever scoped or reset by templateId, so
  // navigating from template A to B while A's replace editor (or
  // deactivation confirmation) was open kept that draft/dialog mounted,
  // relabeled for B, and this screen's own submit handlers would then apply
  // A's typed HTML (or confirm B's deactivation from a confirmation that was
  // never actually shown for B) against B's current `templateId`. Resetting
  // every transient write/dialog state synchronously on a `templateId`
  // transition closes that window.
  useEffect(() => {
    setReplacing(false);
    setReplaceHtml("");
    setReplaceState({ status: "idle" });
    setConfirmingSoftDelete(false);
    setSoftDeleteState({ status: "idle" });
    setAttachmentsOpen(false);
    setRefreshNote(null);
  }, [templateId]);

  const {
    data: templates,
    error: templatesError,
    loading: templatesLoading,
    refetch: refetchTemplates,
  } = useQuery<AdminTemplateSummary[]>({
    key: ["templates"],
    queryFn: () => listTemplates(),
  });

  const template = templates?.find((item) => item.templateId === templateId);

  const attachments = useQuery<AdminAttachmentSummary[]>({
    key: ["template-attachments", templateId ?? ""],
    queryFn: () => listAttachments(templateId as string),
    enabled: Boolean(template),
  });

  const preview = useQuery<AdminTemplatePreview>({
    key: ["template-preview", templateId ?? ""],
    queryFn: () => getTemplatePreview(templateId as string),
    enabled: Boolean(template?.activo),
  });

  // Codex finding (PR #387 review round 3): when React Router reuses this
  // screen navigating directly from one active template to another,
  // `useQuery` retains the PREVIOUS key's `preview.data` while the new
  // fetch is still in flight (state/useQuery.ts never resets `data` on a
  // key change) — this condition rendered template A's HTML under
  // template B's name until B's request finished. `AdminTemplatePreview`
  // carries no `templateId` of its own to associate the data with, so this
  // tracks the templateId a CONFIRMED-fresh `preview.data` object belongs
  // to instead: reset the instant `templateId` changes (before any fetch
  // for it can possibly have resolved yet), and only marked ready once a
  // genuinely NEW data object reference arrives (a fresh fetch's own
  // response — never just "is defined", which a merely-retained stale
  // object also satisfies).
  const previewLastSeenDataRef = useRef<AdminTemplatePreview | undefined>(undefined);
  const [previewReadyForTemplateId, setPreviewReadyForTemplateId] = useState<string | undefined>(undefined);
  useEffect(() => {
    setPreviewReadyForTemplateId(undefined);
  }, [templateId]);
  useEffect(() => {
    if (preview.data !== undefined && preview.data !== previewLastSeenDataRef.current) {
      previewLastSeenDataRef.current = preview.data;
      setPreviewReadyForTemplateId(templateId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- templateId is read for "which template this fresh data belongs to", not as a trigger; preview.data's own identity change is what should drive this effect
  }, [preview.data]);
  const previewDataForCurrentTemplate = previewReadyForTemplateId === templateId ? preview.data : undefined;

  // adv2-slice08 (id 4377321270): this screen owns a potentially large HTML
  // draft (`replaceHtml`) but never registered it with
  // `routeState.ts#useUnsavedChangesBlocker`, unlike the same editor on
  // `TemplatesScreen`/`SegmentDetailScreen`'s edit form — browser Back,
  // reload, or '‹ Plantillas' silently discarded a typed draft with no
  // confirmation. Opening the editor with nothing typed yet is not dirty.
  //
  // Codex finding (PR #387 review round 3): once the save request is
  // pending, this guard still used the default `hardBlock=false`, so
  // accepting the dirty-draft prompt on '‹ Plantillas' (or any other
  // navigation this guard covers) unmounted the screen while
  // `replaceTemplateContent` kept running in the background — its eventual
  // success or failure became invisible and the operator could retry an
  // already-completed replacement, creating an unintended extra version.
  // Same `hardBlock` convention `CampaignCreatePanel` already uses for its
  // own in-flight submit.
  const isReplaceSaving = replaceState.status === "pending";
  const { confirmLeave } = useUnsavedChangesBlocker(replacing && (replaceHtml !== "" || isReplaceSaving), isReplaceSaving);

  const handleReplaceSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (!templateId || replaceState.status === "pending") return;
      setReplaceState({ status: "pending" });
      try {
        await replaceTemplateContent(templateId, { html: replaceHtml });
      } catch (error) {
        setReplaceState({
          status: "error",
          message: error instanceof ApiError ? error.message : "No se pudo reemplazar el contenido.",
        });
        return;
      }
      // Native advisory (TemplateDetailScreen.tsx:~121): the content replace
      // itself already succeeded at this point -- reporting that success
      // (closing the editor, clearing the draft) must not depend on the
      // follow-up refetch below also succeeding. The two used to share one
      // try/catch, so a rejected refetch landed in the catch meant for a
      // failed REPLACE, setting `replaceState` to an error the editor would
      // only ever show the NEXT time it's reopened (it was already closed by
      // then) -- misleadingly attached to a brand-new attempt.
      setReplaceState({ status: "idle" });
      setReplacing(false);
      setReplaceHtml("");
      setRefreshNote(null);
      // Codex review round 4 (P2, PR #387): the replace editor is already
      // closed and this screen's hard navigation block already lifted by the
      // time these follow-up refetches settle -- if the operator has since
      // navigated to a DIFFERENT template (this screen instance reused, same
      // as the soft-delete flow's own `startedForTemplateId` guard), a late
      // completion for THIS (now stale) call must never write into the
      // current, unrelated template's `refreshNote`.
      const startedForTemplateId = templateId;
      try {
        // P2 (Codex PR #387, id 4377321256): refetching only the template
        // list advances the metadata (version number) while the PREVIEW
        // query stays mounted under its own unchanged
        // `['template-preview', templateId]` key — its iframe kept showing
        // the version being replaced. Refetch it too, as part of this same
        // successful mutation.
        //
        // Codex finding (PR #387 review round 3): `useQuery.refetch()`
        // resolves `false` on a failed refresh rather than REJECTING
        // (state/useQuery.ts's own `runFetch` never rethrows) — a bare
        // try/catch here never caught that, so this `Promise.all` could
        // fulfill with a failed refresh and silently show the OLD version
        // metadata/preview with no warning. Inspect both returned booleans
        // instead of relying on rejection; the `catch` stays as a defensive
        // fallback for a genuine throw.
        const [templatesRefreshed, previewRefreshed] = await Promise.all([refetchTemplates(), preview.refetch()]);
        if (currentTemplateIdRef.current !== startedForTemplateId) return;
        if (!templatesRefreshed || !previewRefreshed) {
          setRefreshNote(
            "La plantilla se reemplazó, pero no se pudo actualizar la vista. Actualiza la página o reintenta.",
          );
        }
      } catch {
        if (currentTemplateIdRef.current !== startedForTemplateId) return;
        // The replace succeeded; only the follow-up refresh failed. Surface
        // that separately instead of reopening/relabeling the editor.
        setRefreshNote(
          "La plantilla se reemplazó, pero no se pudo actualizar la vista. Actualiza la página o reintenta.",
        );
      }
    },
    [templateId, replaceHtml, replaceState.status, refetchTemplates, preview],
  );

  // Codex review round 4 (P2, PR #387): the inline Reintentar below used to
  // clear `refreshNote` before starting both refetches, but
  // `useQuery.refetch()` resolves `false` rather than rejecting on a normal
  // fetch failure (same contract the initial post-replace refresh above
  // already accounts for) -- a bare `.catch()` never restored the note when
  // either retry failed. Inspects both returned booleans, same pattern, and
  // scopes a late completion to the templateId the retry started for, same
  // "reused screen instance" guard as the initial refresh.
  const retryRefreshNote = useCallback(() => {
    setRefreshNote(null);
    const startedForTemplateId = templateId;
    void Promise.all([refetchTemplates(), preview.refetch()]).then(
      ([templatesRefreshed, previewRefreshed]) => {
        if (currentTemplateIdRef.current !== startedForTemplateId) return;
        if (!templatesRefreshed || !previewRefreshed) {
          setRefreshNote(
            "La plantilla se reemplazó, pero no se pudo actualizar la vista. Actualiza la página o reintenta.",
          );
        }
      },
      () => {
        if (currentTemplateIdRef.current !== startedForTemplateId) return;
        setRefreshNote(
          "La plantilla se reemplazó, pero no se pudo actualizar la vista. Actualiza la página o reintenta.",
        );
      },
    );
  }, [templateId, refetchTemplates, preview]);

  const confirmSoftDelete = useCallback(async () => {
    if (!templateId) return;
    // Codex finding (PR #387 review round 3): scope every completion below
    // to the templateId this specific call started for — if the operator
    // has since navigated to a DIFFERENT template (this screen instance
    // reused, same as the replace-editor case above), a late result from
    // THIS (now stale) call must never touch the current, unrelated
    // template's write state.
    const startedForTemplateId = templateId;
    setConfirmingSoftDelete(false);
    setSoftDeleteState({ status: "pending" });
    try {
      await softDeleteTemplate(startedForTemplateId);
      if (currentTemplateIdRef.current !== startedForTemplateId) return;
      // Codex finding (PR #387 review round 3): `useQuery.refetch()`
      // resolves `false` on a failed refresh rather than rejecting — a bare
      // `await` here previously completed normally right after
      // `softDeleteState` was already reset to idle, leaving the old active
      // template and its enabled "Desactivar" visible with no warning that
      // the list was never actually refreshed (the operator could submit
      // the already-completed soft-delete again).
      const refreshed = await refetchTemplates();
      if (currentTemplateIdRef.current !== startedForTemplateId) return;
      if (!refreshed) {
        setSoftDeleteState({
          status: "error",
          message: "La plantilla se desactivó, pero no se pudo actualizar la lista. Actualiza la página.",
        });
        return;
      }
      setSoftDeleteState({ status: "idle" });
    } catch (error) {
      if (currentTemplateIdRef.current !== startedForTemplateId) return;
      setSoftDeleteState({
        status: "error",
        message: error instanceof ApiError ? error.message : "No se pudo desactivar la plantilla.",
      });
    }
  }, [templateId, refetchTemplates]);

  const isPending = templatesLoading || (!templates && templatesError === undefined);

  if (isPending) {
    return (
      <>
        <h1>Plantilla</h1>
        <p role="status">Cargando plantilla…</p>
      </>
    );
  }

  if (!templates) {
    return (
      <FeedbackPanel
        variant="error"
        message={describeError(templatesError, "No se pudo cargar la plantilla.")}
        onRetry={refetchTemplates}
      />
    );
  }

  if (!template) {
    return (
      <NotFoundCard
        ariaLabel="Plantilla no encontrada"
        title="Plantilla no encontrada"
        description="La plantilla solicitada no existe o no está disponible para esta cuenta."
        secondaryAction={{ label: "Volver a Plantillas", to: "/plantillas" }}
        primaryAction={{ label: "Ir al inicio", to: "/" }}
      />
    );
  }

  const status = resolveTemplateStatus(template.activo);

  return (
    <section aria-label="Detalle de plantilla" className="v3-template-detail">
      <p>
        <Link
          to="/plantillas"
          onClick={(event) => {
            // Un borrador de reemplazo sucio nunca se descarta en silencio —
            // nunca autoguarda, solo confirma (mismo contrato que
            // `useUnsavedChangesBlocker`).
            if (!confirmLeave()) {
              event.preventDefault();
            }
          }}
        >
          ‹ Plantillas
        </Link>
      </p>
      <header className="v3-template-detail__header">
        <div className="v3-template-detail__title">
          <h1>{template.nombre}</h1>
          <StatusPill label={status.label} tone={status.tone} />
        </div>
        <RequireRole role="Operador">
          <div className="v3-template-detail__actions">
            <button type="button" className="v2-u-target-min" onClick={() => setReplacing(true)}>
              Reemplazar contenido
            </button>
            {/* adv2-slice08 (id 4377321295): an already-inactive template (or
                this same record right after a successful deactivation
                refresh) must not still offer "Desactivar" — the backend
                soft-delete can match the already-inactive row again, so
                confirming would appear to succeed without changing anything.
                Gated on `template.activo`, like the list-card action already
                is. */}
            {template.activo && (
              <button
                type="button"
                className="v2-u-target-min v3-btn-danger-outline"
                onClick={() => setConfirmingSoftDelete(true)}
                disabled={softDeleteState.status === "pending"}
                aria-busy={softDeleteState.status === "pending"}
              >
                Desactivar
              </button>
            )}
          </div>
        </RequireRole>
      </header>
      {softDeleteState.status === "error" && <p role="alert">{softDeleteState.message}</p>}
      {refreshNote && <FeedbackPanel variant="stale" message={refreshNote} onRetry={retryRefreshNote} />}

      <div className="v3-template-detail__grid">
        <section aria-label="Vista previa" className="v3-template-detail__preview">
          <div className="v3-template-detail__preview-header">
            <h2>Vista previa</h2>
            {template.activo && (
              <SegmentedControl
                ariaLabel="Ancho de vista previa"
                options={[
                  { value: "desktop", label: "Escritorio" },
                  { value: "mobile", label: "Móvil" },
                ]}
                value={previewWidth}
                onChange={(value) => setPreviewWidth(value as "desktop" | "mobile")}
              />
            )}
          </div>
          <div className="v3-template-detail__canvas">
            {!template.activo && <p>Plantilla inactiva — sin vista previa.</p>}
            {/* Codex review round 4 (P2, PR #387): both branches below used to
                test `preview.loading`/`!preview.data` -- a retained-but-stale
                `preview.data` (for the PREVIOUS template) is ALSO truthy, so
                once the newly-selected template's own request settled with a
                FAILURE (not just "still pending"), the loading branch kept
                claiming "Cargando vista previa…" forever (its own
                `preview.data !== undefined` stayed true) while the error
                branch's `!preview.data` stayed false, so it never rendered
                either. Both now gate on `previewDataForCurrentTemplate`
                instead: loading while not yet ready AND no error reported
                for the CURRENT template, error once one has been. */}
            {template.activo && !previewDataForCurrentTemplate && preview.error === undefined && (
              <p role="status">Cargando vista previa…</p>
            )}
            {template.activo && !previewDataForCurrentTemplate && preview.error !== undefined && (
              <FeedbackPanel
                variant="error"
                message={describeError(preview.error, "No se pudo cargar la vista previa.")}
                onRetry={preview.refetch}
              />
            )}
            {template.activo && previewDataForCurrentTemplate && preview.stale && (
              <FeedbackPanel variant="stale" message="Datos anteriores de preview." onRetry={preview.refetch} />
            )}
            {template.activo && previewDataForCurrentTemplate && (
              <iframe
                title="Vista previa de plantilla"
                sandbox=""
                srcDoc={previewDataForCurrentTemplate.html}
                className={`v3-template-detail__iframe v3-template-detail__iframe--${previewWidth}`}
              />
            )}
          </div>
        </section>

        <div className="v3-template-detail__side">
          <DetailGroup
            title="Información"
            items={[
              { label: "Producto", value: template.producto },
              { label: "Temporada", value: template.temporada },
              { label: "Versión activa", value: <span className="v2-u-tabular-nums">{template.version}</span> },
              { label: "Estado", value: status.label },
            ]}
            footer={
              <div role="group" aria-label="Adjuntos" className="v3-template-detail__attachments-block">
                <div className="v3-template-detail__attachments-head">
                  <span aria-hidden="true">Adjuntos</span>
                  <button
                    type="button"
                    className="v3-template-detail__attachments-manage"
                    onClick={() => setAttachmentsOpen(true)}
                  >
                    Gestionar
                  </button>
                </div>
                {/* Codex review round 4 (P2, PR #387): closing the management
                    panel refetches this query, but that refetch's own
                    success/failure was discarded -- `attachments.stale`
                    already becomes `true` on exactly this failure, so it is
                    rendered here instead of silently keeping pre-mutation
                    filenames. */}
                {attachments.data && attachments.stale && (
                  <FeedbackPanel variant="stale" message="Datos anteriores de adjuntos." onRetry={attachments.refetch} />
                )}
                {attachments.error !== undefined && !attachments.data ? (
                  <p>No se pudieron cargar los adjuntos.</p>
                ) : !attachments.data ? (
                  <p>Cargando…</p>
                ) : attachments.data.length === 0 ? (
                  <p>Sin adjuntos.</p>
                ) : (
                  <ul aria-label="Adjuntos de la plantilla" className="v2-template-detail__attachments">
                    {attachments.data.map((attachment) => (
                      <li key={attachment.attachmentId}>
                        <span aria-hidden="true">PDF</span> {attachment.nombreArchivo}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            }
          />
        </div>
      </div>

      {replacing && (
        <TemplateReplaceEditor
          templateId={template.templateId}
          templateName={template.nombre}
          currentVersion={template.version}
          html={replaceHtml}
          error={replaceState.status === "error" ? replaceState.message : null}
          pending={replaceState.status === "pending"}
          onHtmlChange={setReplaceHtml}
          onSubmit={(event) => void handleReplaceSubmit(event)}
          onCancel={() => {
            // Codex finding (PR #387 review round 3): the visible Cancel
            // button is already disabled while pending, but the shared
            // `Modal` also invokes this SAME callback on Escape — without
            // this guard, Escape immediately unmounted the editor (clearing
            // its HTML) while `replaceTemplateContent` was still in flight;
            // a later failure then had nowhere to surface and the
            // operator's draft was already gone. Same guard the create
            // modal already applies to its own in-flight request.
            if (replaceState.status === "pending") return;
            setReplacing(false);
            setReplaceHtml("");
          }}
        />
      )}

      {confirmingSoftDelete && (
        <ConfirmDialog
          message={buildSoftDeleteConfirmation(template.nombre)}
          confirmLabel="Desactivar"
          onConfirm={() => void confirmSoftDelete()}
          onCancel={() => setConfirmingSoftDelete(false)}
        />
      )}

      {attachmentsOpen && (
        <TemplateAttachmentsPanel
          templateId={template.templateId}
          onClose={() => {
            setAttachmentsOpen(false);
            // adv2-slice08 (id 4377321278): the management panel keeps and
            // refreshes its OWN local attachment state; the filenames shown
            // in this screen's Adjuntos card come from this SEPARATE
            // `['template-attachments', templateId]` query, which closing
            // the panel never refreshed, leaving it stale after an
            // upload/removal until the whole screen was revisited.
            void attachments.refetch();
          }}
        />
      )}
    </section>
  );
}
